/**
 * Pure derivation helpers for the Intercompany page, built on the single-source
 * /intercompany/ledger payload (per-entry position contributions + an fx_rates
 * map, native -> display currency).
 *
 * Everything the page shows -- the Entries tab's month groups and grand total,
 * and the Positions tab's nets and per-month breakdowns -- derives from ONE
 * filtered/enriched entry list here. There is no second aggregation path, so
 * month sums, grand totals, and each position's running balance reconcile by
 * construction (see intercompanyLedger.test.js).
 *
 * Money rules: amounts arrive as native + fx (str), converted for display via
 * the shared currencyConvert helper. Signs are preserved (positive = receivable,
 * negative = payable); Math.abs is only ever applied at the formatting edge. A
 * currency whose rate is missing is marked unconvertible, never silently 1:1.
 */
import { format } from 'date-fns';
import { convertAmount } from './currencyConvert';

/**
 * Convert one ledger entry's signed native amount to the display currency.
 * @returns {{ value: number|null, unconvertible: boolean }}
 */
// Round to whole cents so every downstream sum is over cent-exact values. This
// makes the displayed month sums, running balances, and grand totals tie out
// exactly (raw float sums otherwise drift by a penny between components and the
// total). Rounding at this single conversion point keeps the one aggregation
// path intact: positions, breakdowns, and entries all round identically.
function round2(v) {
  return Math.round((v + Number.EPSILON) * 100) / 100;
}

export function convertLedgerEntry(entry, displayCurrency, fxRates) {
  const signed = entry.signed_amount ?? 0;
  const currency = entry.currency;
  if (currency === displayCurrency) return { value: round2(signed), unconvertible: false };
  const raw = fxRates ? fxRates[currency] : null;
  const rate = raw != null ? parseFloat(raw) : NaN;
  // No usable rate: mark unconvertible rather than letting currencyConvert fall
  // back to a 1:1 identity (a banned money default).
  if (!rate || rate <= 0) return { value: null, unconvertible: true };
  return {
    value: round2(convertAmount(signed, currency, displayCurrency, fxRates)),
    unconvertible: false,
  };
}

/** Filter ledger entries by counterparty (multi-select) and direction. */
export function filterLedgerEntries(entries, filters) {
  const cps = (filters && filters.counterparties) || [];
  const dir = (filters && filters.direction) || 'all';
  return (entries || []).filter((e) => {
    if (cps.length > 0 && !cps.includes(e.counterparty)) return false;
    if (dir !== 'all' && e.direction !== dir) return false;
    return true;
  });
}

/** Distinct counterparties present in the ledger, for the chips filter. */
export function ledgerCounterparties(entries) {
  const seen = new Map();
  for (const e of entries || []) {
    if (!seen.has(e.counterparty)) seen.set(e.counterparty, e.counterparty_name || e.counterparty);
  }
  return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
}

export function monthLabel(monthKey) {
  if (!monthKey || monthKey === 'unknown') return 'Undated';
  const parts = String(monthKey).split('-');
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  if (!y || !m) return String(monthKey);
  return format(new Date(y, m - 1, 1), 'MMM yyyy');
}

/**
 * Enrich the filtered entries with their converted value once, then group by
 * month (newest first). Month sums, the grand total, and the per-entry values
 * all come from this same enriched list, so:
 *   sum(group.sum) === total.sum === sum(entry.converted)
 * for any filter combination.
 */
export function buildEntriesView(entries, fxRates, displayCurrency, filters) {
  const filtered = filterLedgerEntries(entries, filters);
  const enriched = filtered.map((e) => {
    const c = convertLedgerEntry(e, displayCurrency, fxRates);
    return { ...e, converted: c.value, unconvertible: c.unconvertible };
  });

  const byMonth = new Map();
  for (const e of enriched) {
    const key = e.month_key || 'unknown';
    if (!byMonth.has(key)) byMonth.set(key, []);
    byMonth.get(key).push(e);
  }
  // Newest month first; 'unknown' (undated) sorts last.
  const monthKeys = Array.from(byMonth.keys()).sort((a, b) => {
    if (a === 'unknown') return 1;
    if (b === 'unknown') return -1;
    return a < b ? 1 : a > b ? -1 : 0;
  });

  const groups = monthKeys.map((key) => {
    const es = byMonth.get(key);
    return {
      monthKey: key,
      label: monthLabel(key),
      entries: es,
      count: es.length,
      sum: es.reduce((s, e) => s + (e.converted ?? 0), 0),
      unconvertibleCount: es.filter((e) => e.unconvertible).length,
    };
  });

  const total = {
    count: enriched.length,
    sum: enriched.reduce((s, e) => s + (e.converted ?? 0), 0),
    unconvertibleCount: enriched.filter((e) => e.unconvertible).length,
  };

  return { groups, total, entries: enriched };
}

/**
 * Group ledger entries into positions by (party, counterparty). Each position's
 * net is the sum of its composing entries' converted amounts, so it derives from
 * the SAME entry list as the per-month breakdown below (one aggregation path).
 * The net also equals the /intercompany/positions converted_net (verified).
 */
export function buildPositions(entries, fxRates, displayCurrency) {
  const byGroup = new Map();
  for (const e of entries || []) {
    const key = (e.party || '') + '::' + e.counterparty;
    if (!byGroup.has(key)) {
      byGroup.set(key, { key, party: e.party, party_name: e.party_name, counterparty: e.counterparty, counterparty_name: e.counterparty_name, entries: [] });
    }
    byGroup.get(key).entries.push(e);
  }

  const positions = Array.from(byGroup.values()).map((g) => {
    let net = 0;
    let unconvertibleCount = 0;
    for (const e of g.entries) {
      const c = convertLedgerEntry(e, displayCurrency, fxRates);
      if (c.unconvertible) unconvertibleCount += 1;
      else net += c.value ?? 0;
    }
    return {
      ...g,
      net,
      unconvertible: unconvertibleCount > 0,
      unconvertibleCount,
      direction: net > 0 ? 'receivable' : net < 0 ? 'payable' : 'settled',
    };
  });

  positions.sort((a, b) =>
    (a.counterparty_name || '').localeCompare(b.counterparty_name || '') ||
    (a.party || '').localeCompare(b.party || ''));
  return positions;
}

/**
 * Net positions to exactly ONE row per counterparty, netting across every
 * relationship (near-side party) and currency for that counterparty in the
 * display currency.
 *
 * The row-splitting distinction in the old per-(party, counterparty) grouping is
 * the PARTY: in the All Entities scope a single counterparty (e.g. an owner
 * holding entity) faces several business parties, so it produced several rows.
 * Here each of those becomes a "relationship" sub-position under one netted row.
 *
 * Invariants (all from the SAME entry list, one aggregation path):
 *   counterparty.net === sum(relationship.net)
 *                    === buildPositionBreakdown(counterparty.entries).net
 * Unconvertible entries are excluded identically at every level, so the equality
 * holds regardless of missing rates.
 */
export function buildNettedPositions(entries, fxRates, displayCurrency) {
  // Relationships are the existing per-(party, counterparty) positions.
  const relationships = buildPositions(entries, fxRates, displayCurrency);

  const byCp = new Map();
  for (const rel of relationships) {
    const key = rel.counterparty;
    if (!byCp.has(key)) {
      byCp.set(key, {
        counterparty: rel.counterparty,
        counterparty_name: rel.counterparty_name,
        relationships: [],
        entries: [],
      });
    }
    const g = byCp.get(key);
    // Label each relationship by what distinguishes it (the near-side party and
    // its currency mix), never by the shared counterparty name.
    const currencies = Array.from(new Set((rel.entries || []).map((e) => e.currency)));
    g.relationships.push({
      ...rel,
      relationshipLabel: rel.party_name || rel.party || currencies.join(', ') || 'Relationship',
      currencies,
    });
    g.entries.push(...(rel.entries || []));
  }

  const positions = Array.from(byCp.values()).map((g) => {
    let net = 0;
    let unconvertibleCount = 0;
    for (const rel of g.relationships) {
      net += rel.net;
      unconvertibleCount += rel.unconvertibleCount;
    }
    // Largest exposure first within a counterparty, then label, for stable order.
    g.relationships.sort(
      (a, b) =>
        Math.abs(b.net) - Math.abs(a.net) ||
        (a.relationshipLabel || '').localeCompare(b.relationshipLabel || ''),
    );
    return {
      key: g.counterparty,
      counterparty: g.counterparty,
      counterparty_name: g.counterparty_name,
      relationships: g.relationships,
      entries: g.entries,
      net,
      unconvertible: unconvertibleCount > 0,
      unconvertibleCount,
      direction: net > 0 ? 'receivable' : net < 0 ? 'payable' : 'settled',
    };
  });

  positions.sort((a, b) => (a.counterparty_name || '').localeCompare(b.counterparty_name || ''));
  return positions;
}

/**
 * Per-month breakdown for one position's composing entries: month, entry count,
 * signed movement (converted), and a running balance carried chronologically so
 * the most recent month's running balance equals the position's net. Months with
 * no movement are omitted, but the running balance stays continuous across gaps
 * because it accumulates over the (sparse) chronological sequence. Rows are
 * returned newest-first for display.
 */
export function buildPositionBreakdown(entries, fxRates, displayCurrency) {
  const byMonth = new Map();
  for (const e of entries || []) {
    const key = e.month_key || 'unknown';
    if (!byMonth.has(key)) byMonth.set(key, []);
    byMonth.get(key).push(e);
  }
  // Oldest first for running-balance accumulation; undated ('unknown') first.
  const ascKeys = Array.from(byMonth.keys()).sort((a, b) => {
    if (a === 'unknown') return -1;
    if (b === 'unknown') return 1;
    return a < b ? -1 : a > b ? 1 : 0;
  });

  let running = 0;
  const rows = [];
  for (const key of ascKeys) {
    const es = byMonth.get(key);
    let movement = 0;
    let unconvertibleCount = 0;
    for (const e of es) {
      const c = convertLedgerEntry(e, displayCurrency, fxRates);
      if (c.unconvertible) unconvertibleCount += 1;
      else movement += c.value ?? 0;
    }
    running += movement;
    rows.push({
      monthKey: key,
      label: monthLabel(key),
      count: es.length,
      movement,
      runningBalance: running,
      unconvertibleCount,
      // The composing entries themselves, so a month row can open a
      // drill-down showing exactly WHICH movements make up the figure.
      // Same objects the Entries tab renders -- one ledger, one truth.
      entries: es,
    });
  }

  const net = running;
  rows.reverse(); // newest first
  return { rows, net };
}
